import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogoLockup } from '../components/LogoLockup';
import { useAuthStore } from '../store/authStore';
import { api } from '../api/client';

const P = '#7c6ff7';
const A = '#f97316';
const CYAN = '#00d4ff';
const G = '#22c55e';

export default function WelcomePage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const firstName = user?.name?.split(' ')[0] || '';
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    localStorage.setItem('sf_welcomed', '1');
    api.patch('/auth/seen-welcome').catch(() => {});
    document.title = 'Welcome — StartupScore';
  }, []);

  // Animate through steps
  useEffect(() => {
    const timer = setInterval(() => {
      setActiveStep(prev => (prev < 4 ? prev + 1 : prev));
    }, 600);
    return () => clearInterval(timer);
  }, []);

  const handleStart = async () => {
    setCreating(true);
    setError('');
    try {
      const { data } = await api.post('/pipeline');
      const pipelineId = data?.data?.pipeline?.id || data?.data?.pipeline?._id;
      if (pipelineId) {
        navigate(`/pipeline/${pipelineId}`, { replace: true });
      } else {
        navigate('/pipeline', { replace: true });
      }
    } catch (err: any) {
      if (err?.response?.data?.error?.code === 'ACTIVE_PIPELINE_EXISTS') {
        navigate('/pipeline', { replace: true });
        return;
      }
      setError('Something went wrong. Please try again.');
      setCreating(false);
    }
  };

  const steps = [
    { icon: '🧠', title: 'Founder Profile', desc: 'Personalized to your skills & budget', time: '3 min', color: P },
    { icon: '💡', title: 'Idea Analysis', desc: 'AI structures and scores your concept', time: '5 min', color: CYAN },
    { icon: '🔬', title: 'Reality Check', desc: 'Live market data & competitor scan', time: '8 min', color: '#ec4899' },
    { icon: '🎯', title: 'Positioning', desc: 'Niche, angle & revenue strategy', time: '6 min', color: A },
    { icon: '📋', title: 'Validation Plan', desc: '14-day step-by-step action plan', time: '5 min', color: G },
  ];

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#050510',
      position: 'relative',
      overflow: 'hidden',
      padding: '1rem',
      fontFamily: "'Inter', system-ui, sans-serif",
    }}>
      <style>{`
        @keyframes fadeUp{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}
        @keyframes fadeIn{from{opacity:0}to{opacity:1}}
        @keyframes spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}
        @keyframes shimmer{0%{background-position:-200% 0}100%{background-position:200% 0}}
        @keyframes pulseGlow{0%,100%{box-shadow:0 0 20px rgba(249,115,22,0.3)}50%{box-shadow:0 0 40px rgba(249,115,22,0.5)}}
        @keyframes stepReveal{from{opacity:0;transform:translateX(-10px)}to{opacity:1;transform:translateX(0)}}
      `}</style>

      {/* Ambient background */}
      <div aria-hidden style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(rgba(255,255,255,0.45) 1px, transparent 1px)', backgroundSize: '32px 32px', opacity: 0.05 }} />
        <div style={{ position: 'absolute', width: 800, height: 800, borderRadius: '50%', top: -300, left: -200, background: `radial-gradient(circle, rgba(124,111,247,0.12) 0%, transparent 70%)` }} />
        <div style={{ position: 'absolute', width: 600, height: 600, borderRadius: '50%', bottom: -200, right: -150, background: `radial-gradient(circle, rgba(249,115,22,0.08) 0%, transparent 70%)` }} />
      </div>

      <div style={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: 560, animation: 'fadeUp 0.5s ease' }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <LogoLockup size="lg" />
        </div>

        {/* Welcome card */}
        <div className="r-welcome-card" style={{
          background: 'rgba(13,13,31,0.8)',
          border: '1px solid rgba(255,255,255,0.06)',
          borderRadius: 24,
          padding: '40px 36px',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
        }}>
          {/* Greeting */}
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <h1 style={{
              fontSize: 32,
              fontWeight: 900,
              color: '#f0f0ff',
              marginBottom: 8,
              lineHeight: 1.2,
            }}>
              Welcome{firstName ? `, ${firstName}` : ''}! 🎉
            </h1>
            <p style={{ color: '#8888aa', fontSize: 15, lineHeight: 1.6 }}>
              In 27 minutes, you'll have a scored idea, market analysis, competitive positioning, and a 14-day action plan.
            </p>
          </div>

          {/* Credits badge */}
          <div style={{
            background: 'linear-gradient(135deg, rgba(249,115,22,0.1), rgba(249,115,22,0.05))',
            border: '1px solid rgba(249,115,22,0.25)',
            borderRadius: 14,
            padding: '14px 20px',
            marginBottom: 28,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 12,
          }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10,
              background: 'rgba(249,115,22,0.15)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 18,
            }}>⚡</div>
            <div>
              <div style={{ color: '#f97316', fontSize: 15, fontWeight: 700 }}>25 free credits loaded</div>
              <div style={{ color: '#8888aa', fontSize: 12 }}>Your first full pipeline is free — no payment needed</div>
            </div>
          </div>

          {/* Pipeline steps - animated reveal */}
          <div style={{ marginBottom: 28 }}>
            <div style={{
              fontSize: 11, fontWeight: 700, color: P,
              letterSpacing: 2, textTransform: 'uppercase', marginBottom: 16,
            }}>
              Your 5-Step AI Pipeline
            </div>
            {steps.map((step, i) => (
              <div key={i} style={{
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                padding: '12px 14px',
                borderRadius: 12,
                marginBottom: 6,
                background: i <= activeStep ? `${step.color}08` : 'transparent',
                border: `1px solid ${i <= activeStep ? `${step.color}20` : 'transparent'}`,
                opacity: i <= activeStep ? 1 : 0.35,
                transform: i <= activeStep ? 'translateX(0)' : 'translateX(-8px)',
                transition: 'all 0.4s ease',
              }}>
                <div style={{
                  width: 36, height: 36, borderRadius: 10,
                  background: `${step.color}15`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 18, flexShrink: 0,
                }}>{step.icon}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 14, color: '#f0f0ff', fontWeight: 600 }}>{step.title}</span>
                    <span style={{ fontSize: 11, color: step.color, fontWeight: 500 }}>{step.time}</span>
                  </div>
                  <p style={{ fontSize: 12, color: '#8888aa', marginTop: 1, lineHeight: 1.4 }}>{step.desc}</p>
                </div>
                {i <= activeStep && (
                  <div style={{
                    width: 20, height: 20, borderRadius: '50%',
                    background: `${step.color}20`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 10, color: step.color, flexShrink: 0,
                  }}>✓</div>
                )}
              </div>
            ))}
          </div>

          {/* Journey breadcrumb */}
          <div style={{
            textAlign: 'center',
            padding: '12px 0',
            borderTop: '1px solid rgba(255,255,255,0.06)',
            marginBottom: 8,
          }}>
            <p style={{ color: '#6b7280', fontSize: 12 }}>
              🗺️ Your journey: Pipeline (free) → 14-day experiment → MVP Blueprint
            </p>
          </div>

          {/* Social proof */}
          <div style={{
            textAlign: 'center',
            padding: '8px 0',
            marginBottom: 24,
          }}>
            <p style={{ color: '#6b7280', fontSize: 12 }}>
              Trusted by founders worldwide · AI-powered · Results in 27 minutes
            </p>
          </div>

          {/* Error */}
          {error && (
            <p style={{ color: '#ff4466', fontSize: 13, marginBottom: 12, textAlign: 'center' }}>{error}</p>
          )}

          {/* CTA */}
          <button
            onClick={handleStart}
            disabled={creating}
            style={{
              width: '100%',
              padding: '18px 0',
              borderRadius: 14,
              background: creating ? '#333' : A,
              border: 'none',
              color: '#fff',
              fontSize: 17,
              fontWeight: 700,
              cursor: creating ? 'not-allowed' : 'pointer',
              boxShadow: creating ? 'none' : '0 4px 24px rgba(249,115,22,0.35)',
              opacity: creating ? 0.7 : 1,
              transition: 'all 0.3s',
              animation: creating ? 'none' : 'pulseGlow 3s ease-in-out infinite',
              letterSpacing: 0.3,
            }}
          >
            {creating ? (
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                <span style={{ width: 18, height: 18, border: '2px solid #fff', borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'spin 1s linear infinite' }} />
                Creating your pipeline...
              </span>
            ) : (
              '→ Start My First Pipeline'
            )}
          </button>

          {/* Skip link */}
          <button
            onClick={() => navigate('/startups', { replace: true })}
            style={{
              display: 'block',
              margin: '14px auto 0',
              background: 'none',
              border: 'none',
              color: '#6b7280',
              fontSize: 12,
              cursor: 'pointer',
              textDecoration: 'underline',
              textUnderlineOffset: 3,
            }}
          >
            Skip — explore the dashboard first
          </button>
        </div>
      </div>
    </div>
  );
}
